import { useRef, useCallback } from 'react';
import useWorldStore from '../store/worldStore';
import { ZONES } from '../data/zones';

function findNearestZone(position) {
  if (!position) return null;
  const [px, , pz] = position;
  let best = null;
  let bestDist = Infinity;

  for (const zone of ZONES) {
    const dx = zone.center[0] - px;
    const dz = zone.center[1] - pz;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < zone.radius * 2 && dist < bestDist) {
      bestDist = dist;
      best = zone;
    }
  }
  return best;
}

function findNearestWorkstation(position, zone) {
  if (!position || !zone) return null;
  const [px, , pz] = position;
  let best = null;
  let bestDist = Infinity;

  for (const ws of zone.workstations) {
    const dx = ws[0] - px;
    const dz = ws[1] - pz;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < bestDist) {
      bestDist = dist;
      best = ws;
    }
  }
  return best;
}

export default function useDragAgent() {
  const isDragging = useRef(false);
  const draggedId = useRef(null);
  const mouseDownPos = useRef({ x: 0, y: 0 });
  const dropPosition = useRef(null);
  const restoreSnapshot = useRef(null);

  const onPointerDown = useCallback((event, agentId) => {
    event.stopPropagation();
    isDragging.current = true;
    draggedId.current = agentId;
    mouseDownPos.current = { x: event.clientX ?? 0, y: event.clientY ?? 0 };

    const currentAgent = useWorldStore.getState().agents.find((agent) => agent.id === agentId);
    restoreSnapshot.current = currentAgent
      ? {
          position: Array.isArray(currentAgent.position) ? [...currentAgent.position] : null,
          targetPosition: Array.isArray(currentAgent.targetPosition)
            ? [...currentAgent.targetPosition]
            : currentAgent.targetPosition ?? null,
          state: currentAgent.state,
          currentZoneId: currentAgent.currentZoneId,
          currentTask: currentAgent.currentTask,
          mode: currentAgent.mode,
          activity: currentAgent.activity,
          locationTag: currentAgent.locationTag,
        }
      : null;

    useWorldStore.getState().setDraggedAgent(agentId);
    useWorldStore.getState().updateAgentState(agentId, { state: 'dragged' });

    if (window.__orbitControls) window.__orbitControls.enabled = false;
    document.body.style.cursor = 'grabbing';
  }, []);

  const onPointerMoveR3F = useCallback((event) => {
    if (!isDragging.current) return;
    const pt = event.point;
    if (!pt) return;

    // Clamp to floor bounds
    const dist = Math.sqrt(pt.x * pt.x + pt.z * pt.z);
    let x = pt.x;
    let z = pt.z;
    if (dist > 18) {
      x *= 18 / dist;
      z *= 18 / dist;
    }

    useWorldStore.getState().updateAgentPosition(draggedId.current, [x, 0, z]);

    // Compute drop indicator — nearest workstation in nearest zone
    const zone = findNearestZone([x, 0, z]);
    if (zone) {
      const ws = findNearestWorkstation([x, 0, z], zone);
      dropPosition.current = ws ? [ws[0], ws[1]] : null;
    } else {
      dropPosition.current = null;
    }
  }, []);

  const onPointerUpR3F = useCallback((event) => {
    if (!isDragging.current) return;
    const id = draggedId.current;
    isDragging.current = false;
    draggedId.current = null;

    useWorldStore.getState().setDraggedAgent(null);
    if (window.__orbitControls) window.__orbitControls.enabled = true;
    document.body.style.cursor = '';

    // Check click vs drag
    const dx = (event.clientX ?? 0) - mouseDownPos.current.x;
    const dy = (event.clientY ?? 0) - mouseDownPos.current.y;
    const moved = Math.sqrt(dx * dx + dy * dy);

    if (moved < 5) {
      useWorldStore.getState().updateAgentState(id, { state: 'idle' });
      useWorldStore.getState().selectAgent(id);
      dropPosition.current = null;
      restoreSnapshot.current = null;
      return;
    }

    // Snap to nearest workstation in nearest zone
    const pos = useWorldStore.getState().agents.find((a) => a.id === id)?.position;
    const nearestZone = findNearestZone(pos);
    const ws = nearestZone ? findNearestWorkstation(pos, nearestZone) : null;

    if (ws) {
      useWorldStore.getState().updateAgentPosition(id, [ws[0], 0, ws[1]]);
    }

    useWorldStore.getState().updateAgentState(id, {
      state: 'idle',
      currentZoneId: nearestZone?.id ?? null,
    });

    // If dropped in create zone, bounce back and open creator
    if (nearestZone?.id === 'create') {
      useWorldStore.getState().updateAgentPosition(id, [0, 0, 0]);
      useWorldStore.getState().updateAgentState(id, { state: 'idle', currentZoneId: 'life' });
      useWorldStore.getState().setShowCreator(true);
      dropPosition.current = null;
      restoreSnapshot.current = null;
      return;
    }

    // If dropped in a work zone, set cooldown and open the inline task composer
    if (nearestZone && nearestZone.id !== 'life') {
      if (window.__setDropCooldown) window.__setDropCooldown(id, 10000);
      useWorldStore.getState().openDroppedTaskComposer({
        agentId: id,
        zoneId: nearestZone.id,
        position: {
          x: event.clientX ?? mouseDownPos.current.x,
          y: event.clientY ?? mouseDownPos.current.y,
        },
        restoreSnapshot: restoreSnapshot.current,
      });
      useWorldStore.getState().setAgentIndicator(id, { type: 'task', pulsing: true });
      setTimeout(() => useWorldStore.getState().clearAgentIndicator(id), 5000);
    }

    dropPosition.current = null;
    restoreSnapshot.current = null;
  }, []);

  return { onPointerDown, onPointerMoveR3F, onPointerUpR3F, isDragging, dropPosition };
}
